import { defineStore } from 'pinia'
import { getMeta, setMeta, NetworkError } from '@addictivegroup/addictive-offline'
import { api } from '@/lib/http.js'
import { offline } from '@/lib/offline.js'
import { plain } from '@/lib/plain.js'
import i18n from '@/i18n/index.js'

/**
 * Core's /meta: entity fields, custom fields, field rules, dictionaries, pipelines,
 * currencies, tags, sequences, products. Kept in the replica, so forms and rules work offline.
 */
export const useMeta = defineStore('meta', {
  state: () => ({ data: null, loadedAt: null }),
  getters: {
    ready: (s) => s.data !== null,
    hash: (s) => s.data?.hash || null,
    baseCurrency: (s) => s.data?.currencies?.base || 'PLN',
    activeCurrencies: (s) => s.data?.currencies?.active || ['PLN'],
  },
  actions: {
    async load({ force = false } = {}) {
      if (this.data && !force) return this.data
      try {
        const body = await api('/meta', { timeoutMs: 15000 })
        this.data = body.data
        this.loadedAt = new Date().toISOString()
        await setMeta(offline.db, 'crm_meta', plain(body.data))
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error
        const cached = await getMeta(offline.db, 'crm_meta')
        if (!cached) throw error
        this.data = cached
      }
      return this.data
    },
    entity(entity) {
      return this.data?.entities?.[entity] || { fields: [], links: {}, actions: [], includes: [] }
    },
    fields(entity) {
      return this.entity(entity).fields || []
    },
    field(entity, name) {
      return this.fields(entity).find((f) => f.name === name) || null
    },
    customFields(entity) {
      return this.data?.custom_fields?.[entity] || []
    },
    customField(entity, key) {
      return this.customFields(entity).find((f) => f.key === key) || null
    },
    fieldRules(entity) {
      return this.data?.field_rules?.[entity] || []
    },
    dictionary(key) {
      return this.data?.dictionaries?.[key] || { entries: [] }
    },
    dictionaryLabel(key, entryKey) {
      if (entryKey === null || entryKey === undefined || entryKey === '') return ''
      const entry = this.dictionary(key).entries.find((e) => e.key === entryKey)
      return entry ? this.localized(entry.label) : entryKey
    },
    pipelines(entity) {
      return (this.data?.pipelines || []).filter((p) => p.entity === entity)
    },
    defaultPipeline(entity) {
      const list = this.pipelines(entity)
      return list.find((p) => p.is_default) || list[0] || null
    },
    pipeline(uuid) {
      return (this.data?.pipelines || []).find((p) => p.uuid === uuid) || null
    },
    stage(uuid) {
      for (const p of this.data?.pipelines || []) {
        const s = p.stages.find((st) => st.uuid === uuid)
        if (s) return { ...s, pipeline_uuid: p.uuid }
      }
      return null
    },
    tag(uuid) {
      return (this.data?.tags || []).find((t) => t.uuid === uuid) || null
    },
    tagCategory(uuid) {
      return (this.data?.tag_categories || []).find((c) => c.uuid === uuid) || null
    },
    /** {pl, en} → the text in the interface language, falling back to the other one. */
    localized(value) {
      if (value === null || value === undefined) return ''
      if (typeof value !== 'object') return String(value)
      const locale = i18n.global.locale.value
      return value[locale] || value.pl || value.en || Object.values(value).find(Boolean) || ''
    },
  },
})
